import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Reveal, Overline } from "../components/Reveal";
import { Phone, Mail, MapPin, Clock } from "lucide-react";

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const preselected = searchParams.get("service") || "";
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", service: preselected,
    message: preselected ? `I'm interested in: ${preselected}. ` : "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { api.get("/services").then((r) => setServices(r.data)).catch(() => {}); }, []);

  const submit = (e) => {
    e.preventDefault();
    const subject = `Quote Request${form.service ? ": " + form.service : ""}${form.name ? " - " + form.name : ""}`;
    const body =
      `Name: ${form.name}\n` +
      `Email: ${form.email}\n` +
      `Phone: ${form.phone}\n` +
      `Service of interest: ${form.service}\n\n` +
      `${form.message}\n`;
    window.location.href = `mailto:info@giremodeling.net?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div>
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-36 md:pt-44 pb-16">
        <Reveal>
          <Overline>Contact</Overline>
          <h1 className="mt-6 font-serif font-light text-5xl md:text-7xl leading-[0.95] max-w-4xl">Request your <em className="gold-gradient-text">free quote.</em></h1>
          <p className="mt-8 max-w-2xl text-white/60 font-body text-lg leading-relaxed">
            Tell us about your project. Our Virginia team will get back to you within 24 hours.
          </p>
        </Reveal>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-28">
        <div className="grid lg:grid-cols-2 gap-16">
          <Reveal>
            <div className="space-y-6 font-body">
              <div className="flex items-start gap-4">
                <Phone size={20} className="text-gold mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/40">Phone</p>
                  <a href="tel:+15712688498" className="text-white text-lg link-underline block">+1 (571) 268-8498</a>
                  <a href="tel:+15715012905" className="text-white text-lg link-underline block mt-1">+1 (571) 501-2905</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail size={20} className="text-gold mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/40">Email</p>
                  <a href="mailto:info@giremodeling.net" className="text-white text-lg link-underline">info@giremodeling.net</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin size={20} className="text-gold mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/40">Service Area</p>
                  <p className="text-white text-lg">Proudly serving all of Virginia</p>
                  <p className="text-white/50 text-sm mt-1">Richmond · Arlington · Fairfax · Virginia Beach · Alexandria</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Clock size={20} className="text-gold mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/40">Hours</p>
                  <p className="text-white text-lg">Mon – Sat · 8:00 AM – 6:00 PM</p>
                </div>
              </div>
              <div className="border border-gold/30 bg-[#0a0a0a] p-6 mt-4">
                <p className="font-serif text-2xl">1-Year Workmanship Warranty</p>
                <p className="text-white/55 text-sm mt-2">Every project is licensed, insured and backed in writing.</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <form data-testid="contact-form" onSubmit={submit} className="space-y-8">
              <input data-testid="contact-name" required placeholder="Full name" value={form.name} onChange={set("name")} className="editorial-input" />
              <input data-testid="contact-email" type="email" required placeholder="Email address" value={form.email} onChange={set("email")} className="editorial-input" />
              <input data-testid="contact-phone" placeholder="Phone" value={form.phone} onChange={set("phone")} className="editorial-input" />
              <select data-testid="contact-service" value={form.service} onChange={set("service")} className="editorial-input text-white [&>option]:bg-black">
                <option value="">Service of interest</option>
                {services.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
              </select>
              <textarea data-testid="contact-message" rows={3} placeholder="Tell us about your project" value={form.message} onChange={set("message")} className="editorial-input resize-none" />
              <button data-testid="contact-submit" type="submit" className="w-full bg-gold text-black text-sm font-semibold uppercase tracking-widest py-4 hover:bg-white transition-colors duration-300">
                Email Us
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
